import { useEffect, useState } from 'react';
import type { EmployeesByCourseReportFilterI, CourseEmployeesReportTotalsI, EmployeesByCourseReportI } from 'types';
import { reportsApi } from 'Api/axios/reports-api';
import type { ReportsPaginationI } from 'Pages/reports/components/ReportsDataGrid/types';

const defaultTotals: CourseEmployeesReportTotalsI = {
  enrolledEmployees: 0,
  approvedEmployees: 0,
  pendingEmployees: 0,
  progressPercentage: 0.0,
  trainingHours: 0,
};

interface Props {
  courseId: string;
  init?: boolean;
}

const useCourseEmployeesReportApi = ({ courseId, init = false }: Props) => {
  const [data, setData] = useState<EmployeesByCourseReportI[]>([]);
  const [pagination, setPagination] = useState<ReportsPaginationI | null>(null);
  const [totals, setTotals] = useState<CourseEmployeesReportTotalsI>(defaultTotals);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Obtiene todos los empleados de un curso específico
   */
  const fetchCourseEmployees = async (courseId: string, filters: EmployeesByCourseReportFilterI = {}) => {
    setLoading(true);
    const response = await reportsApi.readEmployeesByCourse(courseId, filters);
    setData(response.data ?? []);
    setPagination(response.pagination ?? null);
    setLoading(false);
    setError(response.error);
  };

  /**
   * Obtiene los datos totales de un curso en específico.
   */
  const fetchCourseEmployeesTotals = async (courseId: string) => {
    setLoading(true);
    const response = await reportsApi.readEmployeesByCourseTotals(courseId);
    setTotals(response.data ?? defaultTotals);
    setLoading(false);
    setError(response.error);
  };

  useEffect(() => {
    if (init) {
      fetchCourseEmployees(courseId, { page: 1, pageSize: 15 });
      fetchCourseEmployeesTotals(courseId);
    }
  }, [init]);

  return {
    data,
    pagination,
    totals,
    loading,
    error,
    fetchCourseEmployees,
    fetchCourseEmployeesTotals,
  };
};

export default useCourseEmployeesReportApi;
